import React, { useMemo } from 'react'
import { Button } from '@/components/ui/button'
import FilterBadge from '@/components/ChatAnalysis/FilterBadge'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { convertFiltersToDisplayItems, FilterType } from '@/utils/filterUtils'
import { setFilters } from '@/redux/slices/gameAnalysisSlice'
import { GameSummaryMetadata } from '@/redux/types/room'

interface SelectedFiltersDisplayProps {
  metadata: GameSummaryMetadata | null
  onClearFilters: () => void
}

const SelectedFiltersDisplay: React.FC<SelectedFiltersDisplayProps> = ({
  metadata,
  onClearFilters,
}) => {
  const { selectedFilters } = useAppSelector((state) => state.gameAnalysis)
  const dispatch = useAppDispatch()
  const selectedFilterItems = useMemo(() => {
    return convertFiltersToDisplayItems(
      selectedFilters,
      metadata,
      true,
      (type, id) => () => {
        const newFilters = { ...selectedFilters }
        switch (type) {
          case FilterType.PLAYER:
            newFilters.player_ids = selectedFilters.player_ids.filter(
              (filterId) => filterId !== id
            )
            break
          case FilterType.TEAM:
            newFilters.team_ids = selectedFilters.team_ids.filter(
              (filterId) => filterId !== id
            )
            break
          case FilterType.PHASE:
            newFilters.phase_ids = selectedFilters.phase_ids.filter(
              (filterId) => filterId !== id
            )
            break
          case FilterType.ROUND:
            newFilters.round_numbers = selectedFilters.round_numbers.filter(
              (filterId) => filterId !== id
            )
            break
        }
        dispatch(setFilters(newFilters))
      }
    )
  }, [metadata, selectedFilters, dispatch])

  const hasActiveFilters = useMemo(
    () => selectedFilterItems.length > 0,
    [selectedFilterItems.length]
  )
  if (!hasActiveFilters) return null

  return (
    <div className='px-1 space-y-2'>
      <div className='flex items-center justify-end'>
        {hasActiveFilters && (
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={onClearFilters}
            className='h-6 px-2 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          >
            Clear all
          </Button>
        )}
      </div>

      <div className='flex flex-wrap gap-2'>
        {selectedFilterItems.map((item, index) => (
          <FilterBadge
            key={`${item.type}-${index}`}
            item={item}
            index={index}
            showRemove={true}
            size='md'
          />
        ))}
      </div>
    </div>
  )
}

export default SelectedFiltersDisplay
